import React, { useState, useEffect, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  getDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
  onSnapshot,
  increment,
  arrayRemove,
  arrayUnion,
  where,
} from "firebase/firestore";
import { db, storage, auth } from "../firebase.config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Button } from "@chakra-ui/react";
import UserContext from "../contexts/user/UserContext";
import { useContext } from "react";
import { Avatar, Text } from "@chakra-ui/react";
import FadeSpinner from "../components/feedback/FadeSpinner";
import { Helmet } from "react-helmet";

const BuyerRequestsFeed = () => {
  const { displayName, dispatch } = useContext(UserContext);

  const isMounted = useRef(true);
  const navigate = useNavigate();
  const [showReplyInput, setShowReplyInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [commentInput, setCommentInput] = useState({});
  const [uploading, setUploading] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});
  const [likedComments, setLikedComments] = useState({});
  const [postLoading, setPostLoading] = useState(false);
  const [newPost, setNewPost] = useState({
    author: displayName,
    description: "",
    propertyType: "",
    budget: "",
    location: "",
    city: "",
    bedrooms: "",
    bathrooms: "",
    area: "",
  });

  // UseEffect to fetch liked posts for the user
  useEffect(() => {
    if (!displayName) return;

    const fetchLikedPosts = async () => {
      const q = query(
        collection(db, "propertyRequests"),
        where("likedBy", "array-contains", displayName)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const likedPostIds = {};
        snapshot.docs.forEach((doc) => {
          likedPostIds[doc.id] = true;
        });
        setLikedPosts(likedPostIds);
      });

      return () => unsubscribe();
    };

    fetchLikedPosts();
  }, [displayName]);

  useEffect(() => {
    if (isMounted) {
      onAuthStateChanged(auth, (user) => {
        if (!user) {
          navigate("/sign-in");
        }
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [isMounted]);

  useEffect(() => {
    if (!displayName) return;

    const fetchLikedComments = async () => {
      const unsubscribe = onSnapshot(collection(db, "propertyRequests"), (snapshot) => {
        const likedCommentIds = {};

        snapshot.docs.forEach((doc) => {
          const post = doc.data();
          const postId = doc.id;

          post.comments?.forEach((comment, index) => {
            if (comment.likedBy?.includes(displayName)) {
              likedCommentIds[`${postId}_${index}`] = true;
            }
          });
        });

        setLikedComments(likedCommentIds);
      });

      return () => unsubscribe();
    };

    fetchLikedComments();
  }, [displayName]);

  // Handle Image Upload
  const handleImageUpload = async (e, postId) => {
    if (!e.target.files[0]) return;

    const file = e.target.files[0];
    const storageRef = ref(storage, `commentImages/${postId}/${file.name}`);
    setUploading(true);

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setCommentInput((prev) => ({
        ...prev,
        [postId]: {
          ...prev[postId],
          imageUrl: downloadURL,
        },
      }));
      setUploading(false);
    } catch (error) {
      console.error("Error uploading image: ", error);
      setUploading(false);
    }
  };

  // Fetch posts from Firestore
  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch({ type: "SET_USER", payload: user.displayName });
      }
    });
    const postsRef = collection(db, "propertyRequests");
    const q = query(postsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedPosts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        showComments: false, // Initialize showComments as false for each post
      }));
      setPosts(updatedPosts);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [dispatch]);

  if (loading) {
    return <FadeSpinner />;
  }

  const handleToggleLikeComment = async (postId, commentIndex) => {
    const postRef = doc(db, "propertyRequests", postId);
    const userId = displayName;

    try {
      const postSnapshot = await getDoc(postRef);
      if (postSnapshot.exists()) {
        const post = postSnapshot.data();
        const comments = post.comments || [];
        const comment = comments[commentIndex];

        if (!comment) return;

        const isLiked = comment.likedBy?.includes(userId);

        const updatedComments = [...comments];
        if (isLiked) {
          updatedComments[commentIndex] = {
            ...comment,
            likes: comment.likes - 1,
            likedBy: comment.likedBy.filter((id) => id !== userId),
          };
        } else {
          updatedComments[commentIndex] = {
            ...comment,
            likes: (comment.likes || 0) + 1,
            likedBy: [...(comment.likedBy || []), userId],
          };
        }

        await updateDoc(postRef, {
          comments: updatedComments,
        });

        setLikedComments((prev) => ({
          ...prev,
          [`${postId}_${commentIndex}`]: !isLiked,
        }));
      }
    } catch (error) {
      console.error("Error toggling like for comment: ", error);
    }
  };

  const handleAddReply = async (postId, commentIndex, replyText) => {
    try {
      const postRef = doc(db, "propertyRequests", postId);
      const post = posts.find((post) => post.id === postId);
      const updatedComments = [...post.comments];
      const reply = {
        text: replyText,
        author: displayName,
        timestamp: new Date().toISOString(),
      };

      updatedComments[commentIndex].replies =
        updatedComments[commentIndex].replies || [];
      updatedComments[commentIndex].replies.push(reply);

      await updateDoc(postRef, {
        comments: updatedComments,
      });

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, comments: updatedComments } : post
        )
      );
      
      // Close the reply input for this specific comment
      setShowReplyInput((prev) => ({
        ...prev,
        [`${postId}_${commentIndex}`]: false,
      }));
    } catch (error) {
      console.error("Error adding reply: ", error);
    }
  };

  // Handle adding a new post
  const handleSavePost = async () => {
    setPostLoading(true);
    if (
      !newPost.description ||
      !newPost.propertyType ||
      !newPost.budget ||
      !newPost.location ||
      !newPost.city
    ) {
      alert("Please fill in all required fields!");
      setPostLoading(false);
      return;
    }

    const postToSave = {
      ...newPost,
      author: displayName,
      likes: 0,
      likedBy: [],
      comments: [],
      timestamp: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, "propertyRequests"), postToSave);
      setNewPost({
        author: displayName,
        description: "",
        propertyType: "",
        budget: "",
        location: "",
        city: "",
        bedrooms: "",
        bathrooms: "",
        area: "",
      });
      setModalOpen(false);
    } catch (error) {
      console.error("Error adding post: ", error);
      alert("Error creating post. Please try again.");
    }
    setPostLoading(false);
  };

  const toggleCommentsVisibility = (postId) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.id === postId
          ? { ...post, showComments: !post.showComments }
          : post
      )
    );
  };

  const handleToggleLike = async (postId) => {
    const postRef = doc(db, "propertyRequests", postId);
    const userId = displayName;

    try {
      const postSnapshot = await getDoc(postRef);
      if (postSnapshot.exists()) {
        const post = postSnapshot.data();
        const isLiked = post.likedBy?.includes(userId);

        if (isLiked) {
          await updateDoc(postRef, {
            likes: increment(-1),
            likedBy: arrayRemove(userId),
          });
        } else {
          await updateDoc(postRef, {
            likes: increment(1),
            likedBy: arrayUnion(userId),
          });
        }

        setLikedPosts((prev) => ({
          ...prev,
          [postId]: !isLiked,
        }));
      }
    } catch (error) {
      console.error("Error toggling like: ", error);
    }
  };

  // Handle Add Comment
  const handleAddComment = async (postId) => {
    const commentData = commentInput[postId] || {};
    if (!commentData.text?.trim() && !commentData.imageUrl) {
      alert("Please add text or upload an image!");
      return;
    }

    const postRef = doc(db, "propertyRequests", postId);
    const updatedPost = posts.find((post) => post.id === postId);
    const updatedComments = [
      ...(updatedPost.comments || []),
      {
        author: displayName,
        text: commentData.text || "",
        imageUrl: commentData.imageUrl || null,
        timestamp: new Date().toISOString(),
        likes: 0,
        likedBy: [],
        replies: [],
      },
    ];

    try {
      await updateDoc(postRef, { comments: updatedComments });

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, comments: updatedComments } : post
        )
      );

      setCommentInput((prev) => ({
        ...prev,
        [postId]: { text: "", imageUrl: null },
      }));
    } catch (error) {
      console.error("Error adding comment: ", error);
      alert("Error adding response. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-green-50 p-6">
      <Helmet>
        <title>Property Requests | EstateGhar</title>
        <meta
          name="description"
          content="Post your property requirements or connect with real estate agents and sellers"
        />
        <meta
          name="keywords"
          content="real estate, property requests, buy property, rent property, real estate agents, property listings, EstateGhar"
        />
        <link rel="canonical" href="https://www.estateghar.com/feed" />
      </Helmet>
      
      <h1 className="text-3xl md:text-4xl text-center my-5 text-green-800 font-bold">
        Property Requests Feed
      </h1>
      
      {/* Button to open the modal */}
      <div className="w-full md:w-auto rounded-md mb-8">
        <button
          onClick={() => setModalOpen(true)}
          className="w-full md:w-[40%] block mx-auto bg-green-700 text-white py-3 px-6 rounded-lg shadow-md hover:bg-green-800 font-semibold transition-colors"
        >
          + Create New Property Request
        </button>
      </div>

      {/* Modal for creating a new property request */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-[1400]">
          <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-3xl max-h-screen overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-4 text-green-800">
              Create a New Property Request
            </h2>

            <div className="space-y-5">
              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-gray-800 font-medium mb-1">
                  Property Requirements Description *
                </label>
                <textarea
                  id="description"
                  placeholder="Describe what you're looking for... e.g., Looking for a 3-bedroom apartment near downtown with parking."
                  className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  rows={3}
                  value={newPost.description || ""}
                  onChange={(e) =>
                    setNewPost({ ...newPost, description: e.target.value })
                  }
                  required
                />
              </div>

              {/* Property Type */}
              <div>
                <label htmlFor="propertyType" className="block text-gray-800 font-medium mb-1">
                  Property Type *
                </label>
                <select
                  id="propertyType"
                  value={newPost.propertyType}
                  onChange={(e) =>
                    setNewPost({ ...newPost, propertyType: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-md text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  required
                >
                  <option value="">Select Property Type</option>
                  <option value="House">House</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Villa">Villa</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Land">Land</option>
                  <option value="Office">Office</option>
                  <option value="Shop">Shop</option>
                </select>
              </div>

              {/* Budget */}
              <div>
                <label htmlFor="budget" className="block text-gray-800 font-medium mb-1">
                  Budget *
                </label>
                <input
                  id="budget"
                  type="text"
                  placeholder="e.g., 50,000,000 PKR"
                  value={newPost.budget || ""}
                  onChange={(e) =>
                    setNewPost({ ...newPost, budget: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  required
                />
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-gray-800 font-medium mb-1">
                  Preferred Location *
                </label>
                <input
                  id="location"
                  type="text"
                  placeholder="e.g., Downtown, Near schools, Commercial area"
                  value={newPost.location}
                  onChange={(e) =>
                    setNewPost({ ...newPost, location: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  required
                />
              </div>

              {/* City */}
              <div>
                <label htmlFor="city" className="block text-gray-800 font-medium mb-1">
                  City *
                </label>
                <input
                  id="city"
                  type="text"
                  placeholder="e.g., Islamabad, Karachi, Lahore"
                  value={newPost.city}
                  onChange={(e) =>
                    setNewPost({ ...newPost, city: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Bedrooms */}
                <div>
                  <label htmlFor="bedrooms" className="block text-gray-800 font-medium mb-1">
                    Bedrooms
                  </label>
                  <input
                    id="bedrooms"
                    type="number"
                    placeholder="e.g., 3"
                    value={newPost.bedrooms || ""}
                    onChange={(e) =>
                      setNewPost({ ...newPost, bedrooms: e.target.value })
                    }
                    className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </div>

                {/* Bathrooms */}
                <div>
                  <label htmlFor="bathrooms" className="block text-gray-800 font-medium mb-1">
                    Bathrooms
                  </label>
                  <input
                    id="bathrooms"
                    type="number"
                    placeholder="e.g., 2"
                    value={newPost.bathrooms || ""}
                    onChange={(e) =>
                      setNewPost({ ...newPost, bathrooms: e.target.value })
                    }
                    className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </div>

                {/* Area */}
                <div>
                  <label htmlFor="area" className="block text-gray-800 font-medium mb-1">
                    Area (sq ft)
                  </label>
                  <input
                    id="area"
                    type="text"
                    placeholder="e.g., 1500"
                    value={newPost.area || ""}
                    onChange={(e) =>
                      setNewPost({ ...newPost, area: e.target.value })
                    }
                    className="w-full p-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-800 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={handleSavePost}
                disabled={postLoading}
                className={`bg-green-600 text-white py-2 px-5 rounded-md hover:bg-green-700 transition font-semibold ${
                  postLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {postLoading ? "Posting..." : "Post Request"}
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className="bg-gray-600 text-white py-2 px-5 rounded-md hover:bg-gray-700 transition font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feed */}
      <div className="max-w-4xl mx-auto space-y-6">
        {posts.length === 0 ? (
          <div className="bg-white shadow-lg rounded-lg p-12 text-center border border-green-100">
            <svg 
              className="w-24 h-24 mx-auto text-green-300 mb-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" 
              />
            </svg>
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Property Requests Yet</h3>
            <p className="text-gray-500 mb-6">Be the first to post a property request and connect with real estate agents!</p>
            <button
              onClick={() => setModalOpen(true)}
              className="bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700 transition font-semibold"
            >
              Create Your First Request
            </button>
          </div>
        ) : (
          posts.map((post) => (
            <div
              key={post.id}
              className="bg-white shadow-lg rounded-lg p-6 border border-green-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex flex-row mb-4">
                <Avatar size="md" name={post.author} bg="green.500" />
                <Text className="mt-2 ml-3" fontSize="lg" color="gray.700" fontWeight="medium">
                  {post.author}
                </Text>
              </div>
              
              <p className="text-lg font-semibold text-gray-800 mb-3 leading-relaxed">
                {post.description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                <p><span className="font-semibold text-green-700">Property Type:</span> {post.propertyType}</p>
                <p><span className="font-semibold text-green-700">Budget:</span> {post.budget}</p>
                <p><span className="font-semibold text-green-700">Location:</span> {post.location}</p>
                <p><span className="font-semibold text-green-700">City:</span> {post.city}</p>
                {post.bedrooms && <p><span className="font-semibold text-green-700">Bedrooms:</span> {post.bedrooms}</p>}
                {post.bathrooms && <p><span className="font-semibold text-green-700">Bathrooms:</span> {post.bathrooms}</p>}
                {post.area && <p><span className="font-semibold text-green-700">Area:</span> {post.area} sq ft</p>}
                <p className="text-xs text-gray-500 col-span-2">
                  Posted: {post.timestamp?.toDate?.().toLocaleString() || "Recently"}
                </p>
              </div>

              {/* Likes and Comments Count */}
              <div className="mt-4 flex justify-between text-sm text-gray-600 border-t border-gray-200 pt-3">
                <span>{post.likes || 0} Interested</span>
                <span>{post.comments?.length || 0} Responses</span>
              </div>

              {/* Action Buttons */}
              <div className="mt-3 flex space-x-4 border-t border-gray-200 pt-3">
                <button
                  onClick={() => handleToggleLike(post.id)}
                  className={`${
                    likedPosts[post.id]
                      ? "text-green-700 font-bold"
                      : "text-green-600 hover:text-green-700"
                  } transition-colors font-medium`}
                >
                  {likedPosts[post.id] ? "✓ Interested" : "I'm Interested"}
                </button>
                <button
                  onClick={() => toggleCommentsVisibility(post.id)}
                  className="text-blue-600 hover:text-blue-700 transition-colors font-medium"
                >
                  {post.showComments ? "Hide Responses" : "View Responses"}
                </button>
              </div>

              {/* Comments Section */}
              {post.showComments && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h3 className="text-md font-semibold mb-3 text-gray-800">Agent Responses</h3>
                  {post.comments && post.comments.length > 0 ? (
                    <ul className="space-y-4">
                      {post.comments.map((comment, index) => (
                        <li
                          key={index}
                          className="bg-green-50 p-4 rounded-lg border border-green-100"
                        >
                          <div className="flex items-center mb-2">
                            <Avatar size="sm" name={comment.author} bg="blue.500" />
                            <p className="ml-2 text-blue-700 font-medium">{comment.author}</p>
                          </div>
                          
                          {comment.imageUrl && (
                            <img
                              src={comment.imageUrl}
                              alt="Property reference"
                              className="w-full max-w-xs h-auto object-cover rounded-md mb-2"
                            />
                          )}
                          
                          <div>
                            <p className="text-gray-700">{comment.text}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(comment.timestamp).toLocaleString()}
                            </p>
                          </div>
                          
                          <div className="mt-3 flex space-x-4">
                            <button
                              onClick={() => handleToggleLikeComment(post.id, index)}
                              className={`${
                                likedComments[`${post.id}_${index}`]
                                  ? "text-green-700 font-bold"
                                  : "text-green-600 hover:text-green-700"
                              } transition-colors text-sm`}
                            >
                              {likedComments[`${post.id}_${index}`] ? "✓ Helpful" : "Helpful"} ({comment.likes || 0})
                            </button>
                            <button
                              onClick={() => setShowReplyInput({
                                ...showReplyInput,
                                [`${post.id}_${index}`]: !showReplyInput[`${post.id}_${index}`]
                              })}
                              className="text-blue-600 hover:text-blue-700 transition-colors text-sm"
                            >
                              Reply
                            </button>
                          </div>
                          
                          {showReplyInput[`${post.id}_${index}`] && (
                            <div className="mt-3">
                              <input
                                type="text"
                                placeholder="Write a reply..."
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && e.target.value.trim() !== "") {
                                    handleAddReply(post.id, index, e.target.value.trim());
                                    e.target.value = "";
                                  }
                                }}
                                className="w-full p-2 border border-gray-300 rounded-md focus:border-green-500 focus:ring-1 focus:ring-green-500"
                              />
                            </div>
                          )}
                          
                          {comment.replies && comment.replies.length > 0 && (
                            <ul className="mt-3 pl-4 space-y-2 border-l-2 border-green-200">
                              {comment.replies.map((reply, replyIndex) => (
                                <li
                                  key={replyIndex}
                                  className="bg-white p-2 rounded-md text-sm"
                                >
                                  <p className="text-blue-600 font-medium">{reply.author}</p>
                                  <p className="text-gray-600">{reply.text}</p>
                                  <p className="text-xs text-gray-400">
                                    {new Date(reply.timestamp).toLocaleString()}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No responses yet. Be the first to respond!</p>
                  )}
                  
                  {/* Add Comment Form */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <textarea
                      placeholder="Respond to this property request... (Agents: You can suggest properties or ask for more details)"
                      className="w-full p-3 border border-gray-300 rounded-md focus:border-green-500 focus:ring-1 focus:ring-green-500"
                      rows={3}
                      value={commentInput[post.id]?.text || ""}
                      onChange={(e) =>
                        setCommentInput({
                          ...commentInput,
                          [post.id]: {
                            ...commentInput[post.id],
                            text: e.target.value,
                          },
                        })
                      }
                    />
                    <div className="flex flex-col md:flex-row items-center justify-between mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, post.id)}
                        className="text-sm p-2"
                      />
                      {uploading && <span className="text-sm text-gray-500">Uploading...</span>}
                      <button
                        onClick={() => handleAddComment(post.id)}
                        className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-medium mt-2 md:mt-0"
                      >
                        Post Response
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BuyerRequestsFeed;